import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  Brain, Briefcase, Phone, Package, Globe, Home, Users, 
  ArrowRight, Instagram, Linkedin, Youtube, FileText, ChevronDown 
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface SubItemType {
  label: string;
  href: string;
  icon?: LucideIcon;
  iconImage?: string;
  image?: string;
  description?: string;
}

interface ProductCardProps {
  subItem: SubItemType;
  index: number;
  isMobile?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ subItem, index, isMobile = false }) => {
  const SubIcon = subItem.icon ?? Package;
  const cardId = isMobile ? `submenu-produtos-mobile-${index}` : `submenu-produtos-${index}`;
  const cardRole = isMobile ? 'button' : 'menuitem';

  const cardHeight = isMobile ? 'h-56' : 'h-[16rem]';
  const cardWidth = isMobile ? 'w-full' : 'w-56';
  const mdHeight = isMobile ? 'md:h-64' : 'md:h-[18.5rem]';
  const mdWidth = isMobile ? 'md:w-52' : 'md:w-60';

  return (
    <motion.button
      id={cardId}
      tabIndex={0}
      role={cardRole}
      aria-label={`Produto ${subItem.label}`}
      className={`relative z-10 flex ${cardHeight} ${cardWidth} ${mdHeight} ${mdWidth} flex-col items-start justify-end overflow-hidden rounded-[2rem] cursor-pointer group border border-white/10`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.1 * index, ease: "easeOut" } }}
      whileHover={{ scale: 1.02, boxShadow: "0 0 40px rgba(139, 92, 246, 0.3)" }}
      whileTap={{ scale: 0.98 }}
      onClick={() => window.location.href = subItem.href}
    >
      <div className="absolute inset-2 border border-white/20 rounded-[1.5rem] z-30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/95 via-black/40 to-transparent transition-opacity duration-300 group-hover:from-black/100" />
      <div className="relative z-40 p-6 w-full text-left">
        <div className="mb-4 transform transition-transform duration-500 group-hover:-translate-y-1">
          {subItem.iconImage ? (
            <img alt={`Icone ${subItem.label}`} src={subItem.iconImage} className="w-10 h-10 object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
          ) : (
            <SubIcon className="w-10 h-10 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
          )}
        </div>
        <span className="text-[10px] font-black tracking-[0.2em] text-purple-400 uppercase mb-2 block opacity-80">Lukos Tecnologia</span>
        <h4 className="text-white font-black text-2xl mb-2 tracking-tight">{subItem.label}</h4>
        <p className="text-white/60 text-xs leading-relaxed max-w-[200px] font-medium mb-4 transition-colors group-hover:text-white/80">{subItem.description || subItem.label}</p>
        <div className="flex items-center gap-2 overflow-hidden h-0 group-hover:h-8 transition-all duration-500 ease-out">
          <span className="text-white text-sm font-bold tracking-wide uppercase">Conhecer</span>
          <ArrowRight className="w-4 h-4 text-white transform group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
      <img src={subItem.image || 'BANNER-HOME-1.png'} alt={subItem.label} className="absolute inset-0 z-10 object-cover w-full h-full transition duration-1000 group-hover:scale-110 filter brightness-90 group-hover:brightness-100" />
    </motion.button>
  );
};

interface PortalDropdownContentProps {
  buttonRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  className: string;
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  centered?: boolean;
}

const PortalDropdownContent: React.FC<PortalDropdownContentProps> = ({ buttonRef, children, className, isOpen, onMouseEnter, onMouseLeave, centered = false }) => {
  const [position, setPosition] = useState<{ top: number; left: number; maxHeight?: number }>({ top: 0, left: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !buttonRef?.current) return;
    const updatePosition = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      let left = rect.left + window.scrollX;
      if (centered && dropdownRef.current) {
        const dropdownWidth = dropdownRef.current.offsetWidth;
        const buttonCenter = rect.left + (rect.width / 2);
        left = buttonCenter - (dropdownWidth / 2) + window.scrollX;
        const maxLeft = window.innerWidth - dropdownWidth - 16;
        left = Math.max(16, Math.min(left, maxLeft));
      }
      setPosition({
        top: rect.bottom + window.scrollY + 12,
        left: left,
        maxHeight: window.innerHeight - (rect.bottom + 24)
      });
    };
    updatePosition();
    requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, buttonRef, centered]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isAnimating) return null;

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className={`${className} ${!isOpen ? 'dropdown-exit' : ''}`}
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
        maxHeight: position.maxHeight ? `${position.maxHeight}px` : 'auto',
        overflowY: position.maxHeight ? 'auto' : 'visible'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>,
    document.body
  );
};

interface MenuItemType {
  label: string;
  hasDropdown: boolean;
  key: string;
  href: string;
  icon: LucideIcon;
  submenu?: SubItemType[];
  isCardDropdown?: boolean;
}

interface PageNavbarProps {
  transparent?: boolean;
}

const PageNavbar: React.FC<PageNavbarProps> = ({ transparent = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const [openLanguageMenu, setOpenLanguageMenu] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState<string | null>(null);
  const closeTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const menuButtonRefs = useRef<Record<string, HTMLElement | null>>({});
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const inDropdownContainer = !!target?.closest?.('.dropdown-container');
      const inPortalDropdown = !!target?.closest?.('.glass-dropdown');
      if (!inDropdownContainer && !inPortalDropdown) {
        setOpenDropdowns({});
        setOpenLanguageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMenuOpen]);

  const toggleDropdown = (key: string) => {
    setOpenDropdowns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDropdownMouseEnter = (key: string) => {
    if (closeTimeoutRef.current[key]) {
      clearTimeout(closeTimeoutRef.current[key]);
      delete closeTimeoutRef.current[key];
    }
    setHoveredDropdown(key);
    setOpenDropdowns(prev => ({ ...prev, [key]: true }));
  };

  const handleDropdownMouseLeave = (key: string) => {
    closeTimeoutRef.current[key] = setTimeout(() => {
      setOpenDropdowns(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      setHoveredDropdown(null);
      delete closeTimeoutRef.current[key];
    }, 150);
  };

  const menuItems: MenuItemType[] = [
    { label: 'Home', hasDropdown: false, key: 'inicio', href: '/', icon: Home },
    { label: 'Sobre nós', hasDropdown: false, key: 'sobre', href: '/sobre-nos', icon: Users },
    { label: 'Soluções', hasDropdown: false, key: 'solucoes', href: '/solucoes', icon: Briefcase },
    { label: 'Blog', hasDropdown: false, key: 'blog', href: '/blog', icon: FileText }
  ];

  return (
    <>
      <style>{`
        :root { --dropdown-transition: cubic-bezier(0.4, 0, 0.2, 1); --dropdown-duration: 300ms; }
        .glass-dropdown {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px) saturate(160%);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.1);
        }
        .menu-item-link { transition: all 0.3s var(--dropdown-transition); position: relative; }
        .menu-item-link:hover { color: #690093; }
        .language-selector-minimalist { position: fixed; top: 1.5rem; right: 1.5rem; z-index: 110; display: flex; align-items: center; gap: 0.5rem; }
        .language-selector-minimalist .globe-button { 
          padding: 0.5rem; 
          color: #690093; 
          background: white; 
          border-radius: 50%; 
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          border: 1px solid rgba(105,0,147,0.1);
          transition: all 0.3s ease;
        }
        .language-selector-minimalist .globe-button:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(105,0,147,0.15);
        }
      `}</style>

      <nav className="fixed left-0 right-0 z-[100] w-full flex items-center justify-center top-6 px-6 transform-gpu transition-all duration-700">
        <div className={`container mx-auto flex items-center justify-between px-8 h-[72px] bg-white/85 backdrop-blur-2xl border border-white shadow-[0_15px_35px_rgba(0,0,0,0.05)] rounded-full max-w-6xl transition-all duration-500 hover:shadow-[0_20px_45px_rgba(0,0,0,0.1)]`}>
          
          {/* Logo Section */}
          <div className="flex-shrink-0 relative z-50">
            <Link to="/" className="flex items-center space-x-2 transition-transform duration-300 hover:opacity-80">
              <img src="logo.png" alt="LUKOS" className="h-[42px] w-auto" />
            </Link>
          </div>

          {/* Navigation Links (Desktop) */}
          <div className="hidden lg:flex flex-1 justify-center ml-12">
            <ul className="flex items-center space-x-2">
              {menuItems.map((item) => {
                const active = location.pathname === item.href || (item.href === '/' && location.pathname === '/');
                return (
                  <li key={item.key} className="relative">
                    <Link
                      to={item.href}
                      onClick={(e) => {
                        if (location.pathname === item.href) {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      className={`flex items-center gap-2 px-5 py-2.5 text-[15px] font-medium transition-all duration-500 rounded-full ${
                        active 
                        ? 'text-[#690093] bg-purple-50/50' 
                        : 'text-slate-600 hover:text-[#690093] hover:bg-slate-50'
                      }`}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Social & Actions (Desktop) */}
          <div className="hidden lg:flex items-center">
            {/* Social Icons Section */}
            <div className="flex items-center space-x-2.5 pr-6 border-r border-slate-200">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-100 text-slate-400 hover:text-[#690093] hover:border-purple-100 hover:bg-purple-50 transition-all duration-300"><Instagram size={17} /></a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-100 text-slate-400 hover:text-[#0A66C2] hover:border-blue-100 hover:bg-blue-50 transition-all duration-300"><Linkedin size={17} /></a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-100 text-slate-400 hover:text-[#FF0000] hover:border-red-100 hover:bg-red-50 transition-all duration-300"><Youtube size={17} /></a>
            </div>

            {/* Login/Action Placeholder - Refined CTA */}
            <div className="pl-6">
              <Link to="/contato" className="bg-[#690093] text-white px-7 py-2.5 rounded-full font-bold text-sm tracking-wide transition-all duration-300 hover:bg-[#500070] hover:shadow-[0_10px_20px_rgba(105,0,147,0.2)] hover:scale-105 active:scale-95 shadow-md">
                Contato
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="w-10 h-10 flex flex-col justify-center items-center space-y-1.5 focus:outline-none bg-slate-50 rounded-full border border-slate-200 transition-colors hover:bg-slate-100"
              aria-label="Abrir menu"
            >
              <span className={`block w-5 h-0.5 bg-slate-600 transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block w-5 h-0.5 bg-slate-600 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-5 h-0.5 bg-slate-600 transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>

          {/* Mobile Menu Overlay */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="fixed inset-0 z-[101] bg-white lg:hidden flex flex-col p-8 pt-24"
              >
                <button 
                  onClick={() => setIsMenuOpen(false)} 
                  className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 text-slate-600"
                >
                  <ArrowRight className="h-6 w-6 rotate-180" />
                </button>
                
                <div className="mb-12">
                  <img src="logo.png" alt="LUKOS" className="h-14 w-auto mb-4" />
                  <div className="h-1 w-12 bg-[#690093] rounded-full" />
                </div>

                <div className="flex flex-col space-y-4 w-full">
                  {menuItems.map((item) => (
                    <Link 
                      key={item.key} 
                      to={item.href} 
                      onClick={() => setIsMenuOpen(false)} 
                      className="flex items-center justify-between w-full px-6 py-5 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-purple-50 transition-all"
                    >
                      <span className="text-xl font-bold text-slate-800">{item.label}</span>
                      <ChevronDown className="h-5 w-5 text-slate-300 -rotate-90" />
                    </Link>
                  ))}
                </div>
                
                <div className="mt-auto grid grid-cols-3 gap-4 mb-20 text-center">
                   <a href="#" className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-50 border border-slate-100 text-slate-400 hover:text-[#690093] transition-all"><Instagram size={24} /></a>
                   <a href="#" className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-50 border border-slate-100 text-slate-400 hover:text-[#0A66C2] transition-all"><Linkedin size={24} /></a>
                   <a href="#" className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-50 border border-slate-100 text-slate-400 hover:text-[#FF0000] transition-all"><Youtube size={24} /></a>
                </div>

                <Link 
                  to="/contato" 
                  onClick={() => setIsMenuOpen(false)} 
                  className="w-full bg-[#690093] text-white py-5 rounded-full font-bold text-lg text-center shadow-lg"
                >
                  Get Started
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      <div className="language-selector-minimalist" style={{ display: isMenuOpen ? 'none' : 'flex' }}>
        <button onClick={() => setOpenLanguageMenu(!openLanguageMenu)} className="globe-button"><Globe className="w-4 h-4" /></button>
        {openLanguageMenu && (
          <ul className="glass-dropdown absolute top-full right-0 mt-2 w-32 rounded-xl py-2 flex flex-col">
            {['PT', 'EN', 'ES'].map(lang => (
              <li key={lang} className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm font-bold text-gray-700">{lang}</li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default PageNavbar;
